import { useApolloClient } from '@apollo/client';
import {
  Checkbox,
  Modal,
  Pagination,
  SearchInput,
  ToggleButton,
  createEventData,
  pushToDataLayer,
  theme,
  useToast,
} from '@arnold/common';
import { ProductCode } from '@arnold/core';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Loading } from '../..';
import {
  FeEventType,
  Features,
  IsAccessTokenValidQuery,
  OrganizationQuery,
  ProcessStatus,
  SurveyGroupRespondentImportInput,
  SurveyGroupWithoutRespondentsQuery,
  TopicGroupTypeCode,
  UserQuery,
  useAddRespondentsToSurveyGroupMutation,
  useDeleteRespondentMutation,
  useLogFeEventMutation,
  useRemoveRespondentFromSurveyGroupMutation,
  useSurveyGroupRespondentsQuery,
} from '../../../generated/hooks';
import { getCreditPriceForSurveyGroup } from '../../../lib/creditPricing';
import { getOrganizationStructure } from '../../../graphql/queries';
import { watchSurveyGroupChangesSubscription } from '../../../graphql/subscriptions';
import { useFeatureFlag } from '../../../lib/helpers/hooks';
import { LoadingCard } from '../../LoadingCard';
import { ErrorPage } from '../../StyledComponents';
import EditParticipantForm, { EditParticipant } from '../../SurveyGroup/EditParticipantForm';
import NewParticipantsForm from '../../SurveyGroup/NewParticipantsForm';
import { Table } from '../../Table/Table';
import { getDataSource } from './getDataSource';
import { ProcessSurveyParticipantsData } from './types';
import { RESPONDENTS_ON_PAGE, getColumns } from './utils';

interface IProps {
  surveyGroup: NonNullable<SurveyGroupWithoutRespondentsQuery['surveyGroup']>;
  topLevelReportAccess?: string | boolean;
  isOnboarding?: boolean;
  onChangePage: (page: number) => void;
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
  organization: UserQuery['user']['organization'] | OrganizationQuery['organization'];
}

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;

  & label {
    margin-bottom: 0;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToggleTitle = styled.div`
  margin-right: ${theme.spacing.f};
`;

const PaginationContainer = styled.div`
  padding: 0 ${theme.spacing.e} ${theme.spacing.e} ${theme.spacing.e};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceText = styled.span`
  font-weight: bold;
  margin-top: 0.5rem;
  color: ${theme.colors.text.primary};
`;

const today = new Date(new Date().toISOString().substring(0, 10) + 'T00:00:00Z');

export const ProcessSurveyParticipants = ({
  surveyGroup,
  topLevelReportAccess,
  isOnboarding,
  onChangePage,
  organization,
  user,
}: IProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const savedCheckedValue = localStorage.getItem(`hideRespondents:${surveyGroup.id}`);

  const [checked, setChecked] = useState(savedCheckedValue ? JSON.parse(savedCheckedValue) : false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<EditParticipant | null>(null);
  const [addedOn, setAddedOn] = useState<Date | null>(today);
  const [newSelectedRespondents, setNewSelectedRespondents] = useState<SurveyGroupRespondentImportInput[]>([]);
  const [runOngoingSteps, setRunOngoingSteps] = useState<string[]>([]);
  const [removeRespondentModal, setRemoveRespondentModal] = useState<EditParticipant | null>(null);
  const [removeRespondentModalCheckboxChecked, setRemoveRespondentModalCheckboxChecked] = useState(false);
  const [logFeEvent] = useLogFeEventMutation();

  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);
  const isHiring = surveyGroup.topicGroup?.typeCode === TopicGroupTypeCode.Hiring || false;
  const handleLogFeEvent = (respondentId: string) => {
    return (eventType: FeEventType) => {
      try {
        logFeEvent({
          variables: {
            eventType,
            payload: {
              respondentId,
              surveyGroupId: surveyGroup?.id,
              organizationId: organization?.id,
            },
          },
        });
      } catch (err) {
        // Do nothing
      }
    };
  };
  const location = useLocation();
  const { t } = useTranslation('surveyGroup');
  const { addToast } = useToast();
  const search = new URLSearchParams(location.search);
  const currentPage = search.get('page') || '1';

  const refetchQuery = useRef(false);
  const variables = {
    surveyGroupId: surveyGroup.id,
    pagination: {
      offset: (Number(currentPage) - 1) * RESPONDENTS_ON_PAGE,
      limit: RESPONDENTS_ON_PAGE,
    },
    searchQuery,
    withoutDeleted: checked,
  };
  const { loading, error, data, previousData, refetch } = useSurveyGroupRespondentsQuery({
    fetchPolicy: 'no-cache',
    variables,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (refetchQuery.current) {
        refetchQuery.current = false;
        refetch();
      }
    },
  });

  const client = useApolloClient();
  useEffect(() => {
    const subscription = client
      .subscribe({
        query: watchSurveyGroupChangesSubscription,
        variables: {
          surveyGroupId: surveyGroup.id,
        },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: () => {
          refetchQuery.current = true;
          refetch(variables);
        },
        error: (error) => {
          refetch(variables);
        },
      });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyGroup.id]);
  const [addRespondents] = useAddRespondentsToSurveyGroupMutation({
    refetchQueries: [
      {
        query: getOrganizationStructure,
      },
    ],
  });

  const [removeRespondents] = useRemoveRespondentFromSurveyGroupMutation({
    refetchQueries: [
      {
        query: getOrganizationStructure,
      },
    ],
  });
  const [deleteRespondentMutation] = useDeleteRespondentMutation();

  const handleChange = async (value: boolean) => {
    if (currentPage && currentPage !== '1') {
      onChangePage(1);
    }
    setChecked(value);
    localStorage.setItem(`hideRespondents:${surveyGroup.id}`, value.toString());
  };

  const handleChangeSearchQuery = (value: string) => {
    if (currentPage && currentPage !== '1' && value !== searchQuery) {
      onChangePage(1);
    }
    setSearchQuery(value);
  };

  if (loading && previousData == null) {
    return <Loading />;
  }

  if (error) {
    return <ErrorPage>{t('common:loadError')}</ErrorPage>;
  }

  const resetModalStates = () => {
    setModalLoading(false);
    setNewSelectedRespondents([]);
    setRunOngoingSteps([]);
    setIsCreateModalOpen(false);
    setAddedOn(today);
    setIsEditModalOpen(null);
  };

  const setEditModalAndAddedOn = (data: EditParticipant | null) => {
    setIsEditModalOpen(data);
    if (data) {
      setAddedOn(data.addedOn ? new Date(data.addedOn) : today);
    }
  };
  const dataSources = getDataSource({
    surveyGroupRespondents: (data || previousData)?.surveyGroupRespondents.data,
    surveyGroup,
    t,
    topLevelReportAccess,
    isOnboarding,
    searchQuery,
    organization,
    setIsEditModalOpen: setEditModalAndAddedOn,
    setRemoveRespondentModal,
    handleLogFeEvent,
  });

  const isCreditsOrg = organization?.productVersion?.product === ProductCode.Credits;
  const creditPrice = getCreditPriceForSurveyGroup(surveyGroup.topicGroup.typeCode);
  const priceFooter =
    isCreditsOrg && creditPrice !== null ? (
      <PriceText>{t('surveyGroupAddParticipants:creditsPerParticipant', { price: creditPrice })}</PriceText>
    ) : undefined;

  return (
    <>
      {loading && <Loading />}
      <FilterContainer>
        <div className="mt-3 mb-6" style={{ maxWidth: 250 }}>
          <SearchInput onChange={handleChangeSearchQuery} value={searchQuery} delay={500} name="respondents-search" />
        </div>
        <ToggleContainer>
          <ToggleContainer>
            <ToggleTitle>{t('hideDeletedRespondents')}</ToggleTitle>
            <ToggleButton
              value={checked}
              onChange={handleChange}
              dataIcom={'hide-respondents-toggle'}
              dataCy={'hide-respondents-toggle'}
            />
          </ToggleContainer>
          {surveyGroup.status !== ProcessStatus.Archived &&
            !isEmployeeManagementDisabled &&
            surveyGroup.topicGroup?.typeCode !== TopicGroupTypeCode.Hiring && (
              <Button
                variant={'primary'}
                onClick={() => {
                  setIsCreateModalOpen(true);
                }}
                className="ml-6"
                data-icom="btn-add-participants"
                data-cy="btn-add-participants"
              >
                {t('addParticipants')}
              </Button>
            )}
        </ToggleContainer>
      </FilterContainer>
      <Table<ProcessSurveyParticipantsData>
        rowDataCypressTestFlag={'respondent-item'}
        columns={getColumns(t, isOnboarding, isHiring)}
        dataSources={dataSources || []}
        selected={undefined}
        renderDetailPanel={(rowData) => rowData.participantDetail}
      />
      <Modal
        show={isCreateModalOpen}
        onHide={resetModalStates}
        title={t('surveyGroupAddParticipants:title')}
        onSubmit={async () => {
          try {
            setModalLoading(true);
            await addRespondents({
              variables: {
                surveyGroupId: surveyGroup.id,
                respondents: newSelectedRespondents,
                addedOn,
                runOngoingSteps,
              },
            });
            addToast(t('addRespondentsSuccess'));
          } catch (e) {
            addToast(t('addRespondentsError'));
            return;
          }
          await refetch(variables);
          resetModalStates();
        }}
        buttons={{
          cancel: { title: t('modal:cancel'), disabled: modalLoading },
          submit: {
            title: t('surveyGroupAddParticipants:save'),
            disabled: newSelectedRespondents.length === 0 || !addedOn || modalLoading,
          },
        }}
        footerContent={priceFooter}
        width={900}
        content={
          modalLoading ? (
            <LoadingCard />
          ) : (
            <NewParticipantsForm
              surveyGroup={surveyGroup}
              addedOn={addedOn}
              setAddedOn={setAddedOn}
              selectedRespondents={newSelectedRespondents}
              setSelectedRespondents={setNewSelectedRespondents}
              organization={organization}
              runOngoingSteps={runOngoingSteps}
              onOngoingPhaseChange={(actualStepId: string) => () => {
                const updatedRunOngoingSteps = runOngoingSteps.filter((stepId) => stepId !== actualStepId);
                if (updatedRunOngoingSteps.length === runOngoingSteps.length) {
                  updatedRunOngoingSteps.push(actualStepId);
                }

                return setRunOngoingSteps(updatedRunOngoingSteps);
              }}
            />
          )
        }
      />
      <Modal
        show={!!isEditModalOpen}
        onHide={resetModalStates}
        title={t('surveyGroupEditParticipant:title')}
        onSubmit={async () => {
          try {
            if (!isEditModalOpen) {
              return;
            }
            setModalLoading(true);
            await addRespondents({
              variables: {
                surveyGroupId: surveyGroup.id,
                respondents: [
                  {
                    id: isEditModalOpen.id,
                    type: isEditModalOpen.type,
                  },
                ],
                addedOn,
                runOngoingSteps,
              },
            });
            addToast(t('editRespondentSuccess'));
          } catch (e) {
            addToast(t('editRespondentError'));
            return;
          }
          await refetch(variables);
          resetModalStates();
        }}
        buttons={{
          cancel: { title: t('modal:cancel'), disabled: modalLoading },
          submit: {
            title: t('modal:save'),
            disabled: !addedOn || modalLoading || isEditModalOpen?.addedOn === addedOn.toISOString(),
          },
        }}
        footerContent={priceFooter}
        width={900}
        content={
          modalLoading ? (
            <LoadingCard />
          ) : (
            <EditParticipantForm
              surveyGroup={surveyGroup}
              addedOn={addedOn}
              setAddedOn={setAddedOn}
              respondent={isEditModalOpen!}
              setRespondent={setIsEditModalOpen}
              organization={organization}
              runOngoingSteps={runOngoingSteps}
              onOngoingPhaseChange={(actualStepId: string) => () => {
                const updatedRunOngoingSteps = runOngoingSteps.filter((stepId) => stepId !== actualStepId);
                if (updatedRunOngoingSteps.length === runOngoingSteps.length) {
                  updatedRunOngoingSteps.push(actualStepId);
                }

                return setRunOngoingSteps(updatedRunOngoingSteps);
              }}
            />
          )
        }
      />
      <Modal
        show={!!removeRespondentModal}
        onHide={() => setRemoveRespondentModal(null)}
        onSubmit={async () => {
          if (!removeRespondentModal) {
            return;
          }
          try {
            setModalLoading(true);
            await removeRespondents({
              variables: {
                respondentId: removeRespondentModal.id,
                surveyGroupId: surveyGroup.id,
                addedOn: removeRespondentModal.addedOn,
              },
            });
            if (removeRespondentModalCheckboxChecked) {
              setRemoveRespondentModalCheckboxChecked(false);
              await deleteRespondentMutation({
                variables: {
                  respondentId: removeRespondentModal.id,
                },
              });
            }
            const { creditsToReturn = 0 } = removeRespondentModal;

            addToast(
              creditsToReturn > 0
                ? t('onboardingRemoveRespondentSuccess', { credits: creditsToReturn })
                : t('removeRespondentSuccess'),
            );
            pushToDataLayer({
              userId: user.id,
              orgId: organization?.id,
              event: 'ux.update-survey-group-remove-respondent-success',
              ...createEventData('survey group', 'removeRespondentSuccess', 'survey group remove respondent success'),
            });
          } catch (e) {
            addToast(t('removeRespondentError'));
            pushToDataLayer({
              userId: user.id,
              orgId: organization?.id,
              event: 'ux.update-survey-group-remove-respondent-error',
              ...createEventData('survey group', 'removeRespondentError', 'survey group remove respondent error'),
            });
          }
          await refetch(variables);
          resetModalStates();
        }}
        title={t('surveyForm:removeRespondentModalTitle')}
        content={
          modalLoading ? (
            <LoadingCard />
          ) : (
            <>
              <p>
                {t('surveyForm:removeRespondentModalText1')}{' '}
                <b>{`${removeRespondentModal?.firstname} ${removeRespondentModal?.surname}`}</b>{' '}
                {t('surveyForm:removeRespondentModalText2')}
              </p>
              <p>
                {t('surveyForm:removeRespondentModalText3')}
                <br />
                {t('surveyForm:removeRespondentModalText4')}
              </p>
              {!removeRespondentModal?.deleted && (
                <Checkbox
                  selected={removeRespondentModalCheckboxChecked}
                  onToggle={() => setRemoveRespondentModalCheckboxChecked(!removeRespondentModalCheckboxChecked)}
                  text={<span>{t('surveyForm:removeRespondentModalCheckboxText')}</span>}
                />
              )}
            </>
          )
        }
        buttons={{
          submit: { title: t('surveyForm:removeRespondentModalSubmit'), disabled: modalLoading },
          cancel: { title: t('modal:cancel'), disabled: modalLoading },
        }}
      />
      {!!data?.surveyGroupRespondents.totalCount && (
        <PaginationContainer>
          <span style={{ marginTop: 20 }}>
            {t('paginationInfoText', {
              total: data.surveyGroupRespondents.totalCount,
              from: (Number(currentPage) - 1) * RESPONDENTS_ON_PAGE + 1,
              to: (Number(currentPage) - 1) * RESPONDENTS_ON_PAGE + data.surveyGroupRespondents.data.length,
            })}
          </span>
          <Pagination
            totalPages={Math.floor((data.surveyGroupRespondents.totalCount - 1) / RESPONDENTS_ON_PAGE) + 1}
            currentPage={Number(currentPage)}
            onPageChange={onChangePage}
          />
        </PaginationContainer>
      )}
    </>
  );
};
